import { apiService } from './apiService';
import { telemetryService, DeviceInventory, DeviceAggregateStats } from './telemetryService';
import {
  Partner,
  Customer,
  Tenant,
  // Legacy support
  Device,
  User,
  PartnerStaffUser,
  AuditLog,
  DashboardOverview,
  ApiResponse,
  PaginatedResponse,
  PartnerProfile,
  PartnerAuthContext,
  AdminPermissions,
  CustomerUserDetail,
  CustomerUserPermissions
} from '../types/partner';

// Partner API Service - Updated for Phase 3 dual-role user model
export class PartnerService {
  
  // Cache for partner auth context to avoid excessive API calls
  private _authContextCache: { data: ApiResponse<PartnerAuthContext> | null; timestamp: number } = { data: null, timestamp: 0 };
  private readonly AUTH_CONTEXT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes cache
  
  // Dashboard & Overview
  async getDashboardOverview(partnerId: string): Promise<ApiResponse<DashboardOverview>> {
    return apiService.get<DashboardOverview>(`/partner/${partnerId}/dashboard`);
  }

  // Partner Management - Updated to use partnerId
  async getPartnerProfile(partnerId: string): Promise<ApiResponse<PartnerProfile>> {
    return apiService.get<PartnerProfile>(`/partner/${partnerId}/profile`);
  }

  async updatePartnerProfile(partnerId: string, partner: Partial<Partner>): Promise<ApiResponse<Partner>> {
    return apiService.put<Partner>(`/partner/${partnerId}/profile`, partner);
  }

  // Partner Authentication - Phase 3 dual-role handling with PartnerUsers mapping
  // Cached to reduce API calls and avoid rate limiting
  async getPartnerAuthContext(forceRefresh: boolean = false): Promise<ApiResponse<PartnerAuthContext>> {
    // Check cache first
    const now = Date.now();
    if (!forceRefresh && this._authContextCache.data && (now - this._authContextCache.timestamp) < this.AUTH_CONTEXT_CACHE_TTL) {
      return this._authContextCache.data;
    }
    
    try {
      const response = await apiService.get<PartnerAuthContext>(`/partnerauth/context`);
      
      // Cache the result
      this._authContextCache = { data: response, timestamp: now };
      
      return response;
    } catch (err) {
      throw err;
    }
  }
  
  // Clear auth context cache (call when user logs out or context changes)
  clearAuthContextCache(): void {
    this._authContextCache = { data: null, timestamp: 0 };
  }

  // Phase 3: Validate dual-role customer access
  async validatePartnerCustomerAccess(): Promise<ApiResponse<{ canAccessCustomerApp: boolean; customerInfo?: Customer }>> {
    try {
      const response = await apiService.get<{ canAccessCustomerApp: boolean; customerInfo?: Customer }>(`/partnerauth/customer-access-token`);
      return response;
    } catch (err) {
      return { success: false, error: 'Failed to validate customer access' };
    }
  }

  // Phase 3: Partner validation endpoint
  async validatePartner(): Promise<ApiResponse<{ isValid: boolean; email: string }>> {
    try {
      const response = await apiService.get<{ isValid: boolean; email: string }>(`/partnerauth/validate`);
      return response;
    } catch (err) {
      return { success: false, error: 'Failed to validate partner' };
    }
  }

  // Customer Management - Updated for Phase 3 with PartnerUsers access control
  async getPartnerCustomers(partnerId: string): Promise<ApiResponse<Customer[]>> {
    try {
      const response = await apiService.get<Customer[]>(`/partner/${partnerId}/customers`);
      return response;
    } catch (err) {
      throw err;
    }
  }

  async getCustomerDetails(partnerId: string, customerId: string): Promise<ApiResponse<Customer>> {
    return apiService.get<Customer>(`/partner/${partnerId}/customer/${customerId}`);
  }

  async getCustomerLogo(partnerId: string, customerId: string): Promise<ApiResponse<{ logoUrl: string }>> {
    return apiService.get<{ logoUrl: string }>(`/partner/${partnerId}/customer/${customerId}/logo`);
  }

  async saveCustomerLogo(
    partnerId: string,
    customerId: string,
    logoUrl: string | null
  ): Promise<ApiResponse<void>> {
    return apiService.post<void>(`/partner/${partnerId}/customer/${customerId}/logo`, { logoUrl });
  }

  async uploadCustomerLogo(
    partnerId: string,
    customerId: string,
    file: File
  ): Promise<ApiResponse<{ imageUrl: string }>> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiService.http.post<ApiResponse<{ imageUrl: string }>>(
      `/partner/${partnerId}/customer/${customerId}/upload-logo`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  }

  async addCustomer(partnerId: string, customer: Omit<Customer, 'customerId' | 'createdAt'>): Promise<ApiResponse<Customer>> {
    return apiService.post<Customer>(`/partner/${partnerId}/customers`, customer);
  }

  async updateCustomer(partnerId: string, customerId: string, customer: Partial<Customer>): Promise<ApiResponse<Customer>> {
    return apiService.put<Customer>(`/partner/${partnerId}/customer/${customerId}`, customer);
  }

  async removeCustomer(partnerId: string, customerId: string): Promise<ApiResponse<void>> {
    return apiService.delete<void>(`/partner/${partnerId}/customer/${customerId}`);
  }

  // Legacy Tenant methods for backward compatibility
  async getPartnerTenants(partnerId: string): Promise<ApiResponse<Tenant[]>> {
    return this.getPartnerCustomers(partnerId);
  }

  async getTenantDetails(partnerId: string, customerId: string): Promise<ApiResponse<Tenant>> {
    return this.getCustomerDetails(partnerId, customerId);
  }

  async addTenant(partnerId: string, tenant: Omit<Tenant, 'customerId' | 'createdAt'>): Promise<ApiResponse<Tenant>> {
    return this.addCustomer(partnerId, tenant);
  }

  async updateTenant(partnerId: string, customerId: string, tenant: Partial<Tenant>): Promise<ApiResponse<Tenant>> {
    return this.updateCustomer(partnerId, customerId, tenant);
  }

  async removeTenant(partnerId: string, customerId: string): Promise<ApiResponse<void>> {
    return this.removeCustomer(partnerId, customerId);
  }

  // Device Management - Phase 3 with proper access control
  // Now routes through func-aitryx-telemetry for device queries
  
  /**
   * Get all devices for a partner across all their customers.
   * This aggregates inventory data from the telemetry function.
   * @deprecated Use getCustomerDeviceInventory for per-customer queries
   */
  async getPartnerDevices(
    partnerId: string, 
    filters?: {
      customerId?: string;
      status?: string;
      page?: number;
      pageSize?: number;
      search?: string;
    }
  ): Promise<PaginatedResponse<Device>> {
    // If customerId is specified, use telemetry function directly
    if (filters?.customerId) {
      try {
        const response = await telemetryService.getCustomerDeviceInventory(filters.customerId);
        if (response.success && response.data) {
          // Convert DeviceInventory to Device format
          const devices: Device[] = response.data.map(inv => this.inventoryToDevice(inv));
          
          // Apply client-side filtering
          let filtered = devices;
          if (filters.search) {
            const search = filters.search.toLowerCase();
            filtered = filtered.filter(d => 
              d.name?.toLowerCase().includes(search) ||
              d.model?.toLowerCase().includes(search) ||
              d.serialNumber?.toLowerCase().includes(search)
            );
          }
          if (filters.status) {
            filtered = filtered.filter(d => d.status === filters.status);
          }
          
          // Apply pagination
          const page = filters.page || 1;
          const pageSize = filters.pageSize || 50;
          const start = (page - 1) * pageSize;
          const paged = filtered.slice(start, start + pageSize);
          
        return {
          success: true,
          data: paged,
          totalCount: filtered.length,
          currentPage: page,
          pageSize,
          totalPages: Math.ceil(filtered.length / pageSize),
        };
        }
      } catch (error) {
        // Fall back to Core API
      }
    }
    
    // Fallback to Core API for cross-customer queries
    return apiService.getPaginated<Device>(`/partner/${partnerId}/devices`, filters);
  }

  /**
   * Get device inventory for a specific customer (lightweight, no real-time metrics).
   * Optimized for partner portal device lists with caching.
   */
  async getCustomerDeviceInventory(customerId: string): Promise<ApiResponse<DeviceInventory[]>> {
    return telemetryService.getCustomerDeviceInventory(customerId);
  }

  /**
   * Get aggregate statistics for a customer's device fleet.
   */
  async getCustomerDeviceOverview(customerId: string): Promise<ApiResponse<DeviceAggregateStats>> {
    return telemetryService.getCustomerDeviceOverview(customerId);
  }

  /**
   * Convert DeviceInventory to legacy Device format.
   */
  private inventoryToDevice(inv: DeviceInventory): Device {
    return {
      id: inv.id,
      name: inv.name,
      customerId: inv.customerId,
      model: inv.model || 'Unknown',
      ownerUserId: inv.ownerUserId || '',
      firstSeenUtc: inv.firstSeenUtc || '',
      lastSeenUtc: inv.lastSeenUtc || '',
      lastBlobPath: '',
      source: inv.source === 'intune' ? 'Intune' : 'Container',
      manufacturer: inv.manufacturer,
      serialNumber: inv.serialNumber,
      platform: inv.platform,
      osVersion: inv.osVersion,
      agentVersion: inv.agentVersion,
      currentUserName: inv.currentUserName,
      currentUserEmail: inv.currentUserEmail,
      hostName: inv.hostName,
      cpuBrand: inv.cpuBrand,
      cpuCores: inv.cpuCores,
      architecture: inv.architecture,
      totalMemoryGB: inv.totalMemoryGB,
    };
  }



  async updateDevice(partnerId: string, deviceId: string, device: Partial<Device>): Promise<ApiResponse<Device>> {
    return apiService.put<Device>(`/partner/${partnerId}/device/${deviceId}`, device);
  }



  // User Management - Users belong to exactly one customer via User.CustomerId
  async getPartnerUsers(
    partnerId: string,
    filters?: {
      customerId?: string;
      role?: string;
      status?: string;
      page?: number;
      pageSize?: number;
      search?: string;
    }
  ): Promise<PaginatedResponse<User>> {
    try {
      const response = await apiService.getPaginated<User>(`/partner/${partnerId}/users`, filters);
      return response;
    } catch (err) {
      throw err;
    }
  }

  // Add new user to a customer (partner must manage that customer)
  async addUserToCustomer(
    partnerId: string, 
    customerId: string, 
    userData: {
      firstName: string;
      lastName: string;
      email: string;
      isAdmin?: boolean;
      isSuperAdmin?: boolean;
    }
  ): Promise<ApiResponse<User>> {
    try {
      const response = await apiService.post<User>(`/partner/${partnerId}/customer/${customerId}/users`, userData);
      return response;
    } catch (err) {
      throw err;
    }
  }

  // Full detail for one customer user: account fields, module permissions and tenant features
  async getCustomerUserDetail(
    partnerId: string,
    customerId: string,
    userId: string
  ): Promise<ApiResponse<CustomerUserDetail>> {
    return apiService.get<CustomerUserDetail>(`/partner/${partnerId}/customer/${customerId}/users/${userId}`);
  }

  // Update a customer user's name, status, admin and super admin flags
  async updateCustomerUser(
    partnerId: string,
    customerId: string,
    userId: string,
    userData: {
      firstName?: string;
      lastName?: string;
      isAdmin?: boolean;
      isSuperAdmin?: boolean;
      status?: 'Active' | 'Inactive';
    }
  ): Promise<ApiResponse<User>> {
    return apiService.patch<User>(`/partner/${partnerId}/customer/${customerId}/users/${userId}`, userData);
  }

  // Update a customer user's module permissions (same blob aitryx-customer reads).
  // Fields left out are kept as they are on the server.
  async updateCustomerUserPermissions(
    partnerId: string,
    customerId: string,
    userId: string,
    permissions: {
      adminPermissions?: AdminPermissions;
      knowledgeBaseAccess?: string[];
      accessGroups?: string[];
    }
  ): Promise<ApiResponse<CustomerUserPermissions>> {
    return apiService.patch<CustomerUserPermissions>(
      `/partner/${partnerId}/customer/${customerId}/users/${userId}/permissions`,
      permissions
    );
  }

  // Remove user from customer
  async removeUserFromCustomer(
    partnerId: string,
    customerId: string,
    userId: string
  ): Promise<ApiResponse<void>> {
    try {
      const response = await apiService.delete<void>(`/partner/${partnerId}/customer/${customerId}/users/${userId}`);
      return response;
    } catch (err) {
      throw err;
    }
  }

  // Customer Management - Enhanced for contact editing
  async updateCustomerContact(
    partnerId: string,
    customerId: string,
    contactData: {
      adminFirstName?: string;
      adminLastName?: string;
      adminEmail?: string;
      contactNumber?: string;
      country?: string;
      // Address fields
      addressLine1?: string;
      addressLine2?: string;
      city?: string;
      state?: string;
      postalCode?: string;
      industry?: string;
      notes?: string;
      // Note: customerName and identifiers are read-only
    }
  ): Promise<ApiResponse<Customer>> {
    try {
      const response = await apiService.put<Customer>(`/partner/${partnerId}/customer/${customerId}/contact`, contactData);
      return response;
    } catch (err) {
      throw err;
    }
  }

  // Device Management - Enhanced for container-based access
  async getCustomerDevices(
    partnerId: string,
    customerId: string,
    filters?: {
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
    }
  ): Promise<PaginatedResponse<Device>> {
    try {
      const response = await apiService.getPaginated<Device>(`/partner/${partnerId}/customer/${customerId}/devices`, filters);
      return response;
    } catch (err) {
      throw err;
    }
  }

  // Get devices across all managed customers - now routes through telemetry function
  async getAllPartnerDevices(
    partnerId: string,
    filters?: {
      customerId?: string;
      page?: number;
      pageSize?: number;
      search?: string;
      status?: string;
    }
  ): Promise<PaginatedResponse<Device & { customerName: string }>> {
    try {
      // If customerId is specified, use telemetry function for better performance
      if (filters?.customerId) {
        const telemetryResponse = await telemetryService.getCustomerDevices(filters.customerId);
        
        if (telemetryResponse.success && telemetryResponse.data) {
          // Convert telemetry snapshots to Device format
          let devices: (Device & { customerName: string })[] = telemetryResponse.data.map(snapshot => ({
            id: snapshot.id,
            name: snapshot.name || snapshot.deviceName || 'Unknown',
            customerId: snapshot.customerId,
            customerName: '', // Will be filled from context
            model: snapshot.model || 'Unknown',
            ownerUserId: snapshot.userId || '',
            firstSeenUtc: snapshot.firstSeen || '',
            lastSeenUtc: snapshot.lastSeen || '',
            lastBlobPath: '',
            source: 'Container' as const,
            deviceType: 'aitryx' as const,
            manufacturer: snapshot.manufacturer,
            serialNumber: snapshot.serialNumber,
            platform: snapshot.platform,
            osVersion: snapshot.osVersion,
            agentVersion: snapshot.agentVersion,
            currentUserName: snapshot.currentUserName,
            currentUserEmail: snapshot.currentUserEmail,
            operatingSystem: snapshot.platform,
            hostName: snapshot.hostName,
            cpuBrand: snapshot.cpuBrand,
            cpuCores: snapshot.cpuCores,
            architecture: snapshot.architecture,
            totalMemoryGB: snapshot.totalMemoryGB,
            status: snapshot.status as 'Online' | 'Offline' | 'Warning' | undefined,
            cpuUsage: snapshot.cpuUsagePercent,
            memoryUsage: snapshot.memoryUsedPercent,
            maxDiskUsage: snapshot.diskFreePercent ? (100 - snapshot.diskFreePercent) : undefined,
            uptimeSeconds: snapshot.uptimeSeconds,
          }));
          
          // Apply client-side filtering
          if (filters.search) {
            const search = filters.search.toLowerCase();
            devices = devices.filter(d => 
              d.name?.toLowerCase().includes(search) ||
              d.model?.toLowerCase().includes(search) ||
              d.serialNumber?.toLowerCase().includes(search)
            );
          }
          if (filters.status) {
            devices = devices.filter(d => d.status === filters.status);
          }
          
          // Apply pagination
          const page = filters.page || 1;
          const pageSize = filters.pageSize || 50;
          const start = (page - 1) * pageSize;
          const paged = devices.slice(start, start + pageSize);
          
          return {
            success: true,
            data: paged,
            totalCount: devices.length,
            currentPage: page,
            pageSize,
            totalPages: Math.ceil(devices.length / pageSize),
          };
        }
        
      }

      // Fallback to Core API for cross-customer queries or when telemetry fails
      const response = await apiService.getPaginated<Device & { customerName: string }>(`/partner/${partnerId}/devices`, filters);
      return response;
    } catch (err) {
      throw err;
    }
  }

  // Get device details with telemetry - now routes through telemetry function
  async getDeviceDetailsWithTelemetry(
    partnerId: string,
    customerId: string,
    deviceId: string
  ): Promise<ApiResponse<Device & { telemetry?: any; lastUpdate?: string }>> {
    try {
      // Use telemetry function for device details
      const telemetryResponse = await telemetryService.getDeviceLatest(deviceId, customerId);
      
      if (telemetryResponse.success && telemetryResponse.data) {
        const snapshot = telemetryResponse.data;
        return {
          success: true,
          data: {
            id: snapshot.id,
            name: snapshot.name || snapshot.deviceName || 'Unknown',
            customerId: snapshot.customerId,
            model: snapshot.model || 'Unknown',
            ownerUserId: snapshot.userId || '',
            firstSeenUtc: snapshot.firstSeen || '',
            lastSeenUtc: snapshot.lastSeen || '',
            lastBlobPath: '',
            source: 'Container' as const,
            deviceType: 'aitryx' as const,
            manufacturer: snapshot.manufacturer,
            serialNumber: snapshot.serialNumber,
            platform: snapshot.platform,
            osVersion: snapshot.osVersion,
            agentVersion: snapshot.agentVersion,
            currentUserName: snapshot.currentUserName,
            currentUserEmail: snapshot.currentUserEmail,
            operatingSystem: snapshot.platform,
            hostName: snapshot.hostName,
            cpuBrand: snapshot.cpuBrand,
            cpuCores: snapshot.cpuCores,
            architecture: snapshot.architecture,
            totalMemoryGB: snapshot.totalMemoryGB,
            status: snapshot.status as 'Online' | 'Offline' | 'Warning' | undefined,
            cpuUsage: snapshot.cpuUsagePercent,
            memoryUsage: snapshot.memoryUsedPercent,
            uptimeSeconds: snapshot.uptimeSeconds,
            telemetry: snapshot,
            lastUpdate: snapshot.lastSeen,
          },
        };
      }
      
      return { success: false, error: 'Device not found' };
    } catch (err) {
      throw err;
    }
  }

  /**
   * @deprecated Use getDeviceDetailsWithTelemetry instead
   */
  async getDeviceDetails(
    partnerId: string,
    customerId: string,
    deviceId: string
  ): Promise<ApiResponse<Device & { telemetry?: any; lastUpdate?: string }>> {
    // Redirect to new telemetry-based method
    return this.getDeviceDetailsWithTelemetry(partnerId, customerId, deviceId);
  }

  // Generate SAS token for customer's device container (read-only access)
  async getCustomerDeviceContainerAccess(
    partnerId: string,
    customerId: string
  ): Promise<ApiResponse<{ 
    sasUrl: string; 
    containerName: string; 
    virtualDirectory: string;
    virtualPath: string;
    expiresAt: string;
    accessType: string;
    note: string;
    structure: string;
  }>> {
    try {
      const response = await apiService.get<{ 
        sasUrl: string; 
        containerName: string; 
        virtualDirectory: string;
        virtualPath: string;
        expiresAt: string;
        accessType: string;
        note: string;
        structure: string;
      }>(
        `/partner/${partnerId}/customer/${customerId}/devices/container-access`
      );
      return response;
    } catch (err) {
      throw err;
    }
  }

  // Audit & Activity - Phase 3 with proper access control
  async getAuditLogs(
    partnerId: string,
    filters?: {
      customerId?: string;
      eventType?: string;
      startDate?: string;
      endDate?: string;
      page?: number;
      pageSize?: number;
    }
  ): Promise<PaginatedResponse<AuditLog>> {
    try {
      const response = await apiService.getPaginated<AuditLog>(`/partner/${partnerId}/audit-logs`, filters);
      return response;
    } catch (err) {
      throw err;
    }
  }

  async getRecentActivity(partnerId: string, limit: number = 10): Promise<ApiResponse<AuditLog[]>> {
    return apiService.get<AuditLog[]>(`/partner/${partnerId}/activity/recent`, { limit });
  }

  // Security & Alerts
  async getSecurityAlerts(partnerId: string): Promise<ApiResponse<any[]>> {
    return apiService.get<any[]>(`/partner/${partnerId}/security/alerts`);
  }

  async getSecuritySummary(partnerId: string): Promise<ApiResponse<any>> {
    return apiService.get<any>(`/partner/${partnerId}/security/summary`);
  }

  // Reports & Export
  async exportData(
    partnerId: string, 
    exportType: 'devices' | 'users' | 'audit' | 'customers',
    filters?: any
  ): Promise<ApiResponse<{ downloadUrl: string }>> {
    return apiService.post<{ downloadUrl: string }>(`/partner/${partnerId}/export/${exportType}`, filters);
  }

  async generateReport(
    partnerId: string,
    reportType: string,
    parameters: any
  ): Promise<ApiResponse<{ reportId: string }>> {
    return apiService.post<{ reportId: string }>(`/partner/${partnerId}/reports/generate`, {
      reportType,
      parameters
    });
  }

  // Health & Status
  async getSystemStatus(): Promise<ApiResponse<{ status: string; services: any[] }>> {
    return apiService.get<{ status: string; services: any[] }>('/system/status');
  }

  // Partner-Customer Access Validation - Phase 3
  async validatePartnerAccess(partnerId: string, customerId: string): Promise<ApiResponse<{ hasAccess: boolean }>> {
    try {
      const response = await apiService.get<{ hasAccess: boolean }>(`/partner/${partnerId}/validate-access/${customerId}`);
      return response;
    } catch (err) {
      return { success: false, error: 'Failed to validate access' };
    }
  }

  // Phase 3: Debug endpoint for troubleshooting
  async debugToken(): Promise<ApiResponse<any>> {
    try {
      const response = await apiService.get<any>(`/partnerauth/debug`);
      return response;
    } catch (err) {
      return { success: false, error: 'Failed to debug token' };
    }
  }

  // Debug what the partner API is actually returning for devices
  async debugPartnerDevices(partnerId: string, customerId: string): Promise<ApiResponse<any>> {
    try {
      const response = await apiService.get<any>(`/partner/${partnerId}/devices`, { customerId });
      return response;
    } catch (err) {
      return { success: false, error: 'Failed to debug partner devices API' };
    }
  }

  // Account Manager Management
  async getPartnerStaff(partnerId: string): Promise<ApiResponse<PartnerStaffUser[]>> {
    try {
      const response = await apiService.get<PartnerStaffUser[]>(`/partner/${partnerId}/staff`);
      return response;
    } catch (err) {
      throw err;
    }
  }

  async assignAccountManager(
    partnerId: string,
    customerId: string,
    userId: string | null
  ): Promise<ApiResponse<{ amFirstName?: string; amLastName?: string; amEmail?: string; amNumber?: string }>> {
    try {
      const response = await apiService.patch<{ amFirstName?: string; amLastName?: string; amEmail?: string; amNumber?: string }>(
        `/partner/${partnerId}/customer/${customerId}/account-manager`,
        { userId }
      );
      return response;
    } catch (err) {
      throw err;
    }
  }

  // Partner Staff Management
  async addPartnerStaff(
    partnerId: string,
    staffData: {
      email: string;
      firstName: string;
      lastName: string;
      contactNumber?: string;
      isAdmin?: boolean;
    }
  ): Promise<ApiResponse<PartnerStaffUser>> {
    try {
      const response = await apiService.post<PartnerStaffUser>(
        `/partner/${partnerId}/staff`,
        staffData
      );
      return response;
    } catch (err) {
      throw err;
    }
  }

  async removePartnerStaff(
    partnerId: string,
    partnerUserId: string
  ): Promise<ApiResponse<void>> {
    try {
      const response = await apiService.delete<void>(
        `/partner/${partnerId}/staff/${partnerUserId}`
      );
      return response;
    } catch (err) {
      throw err;
    }
  }

  async updatePartnerStaff(
    partnerId: string,
    partnerUserId: string,
    updates: {
      firstName?: string;
      lastName?: string;
      isAdmin?: boolean;
    }
  ): Promise<ApiResponse<PartnerStaffUser>> {
    try {
      const response = await apiService.patch<PartnerStaffUser>(
        `/partner/${partnerId}/staff/${partnerUserId}`,
        updates
      );
      return response;
    } catch (err) {
      throw err;
    }
  }
}

// Export singleton instance
export const partnerService = new PartnerService();
export default partnerService;
